import { useEffect, useRef, useState } from 'react';
import { useOtsColorScheme } from '../theme/OtsTheme';

export type OtsAppLogoProps = {
  label: string;
  iconSrc: string;
  logoSrc: string;
  darkIconSrc?: string;
  darkLogoSrc?: string;
  logoAspectRatio: number;
  onClick: () => void;
};

/** Width of the collapsed mark; only the pointer over this part counts as hovering the logo. */
const ICON_ZONE_PX = 40;
/** A pointer that only passes over the mark on its way somewhere else doesn't expand it. */
const HOVER_DELAY_MS = 150;

/**
 * Transparent home mark that expands into the wordmark in the navigation flow. Hover counts only over the
 * icon itself: the wordmark widens the button, so otherwise a pointer heading for the control beside it
 * (the category switcher) would stay "on" the logo and keep it open.
 */
export const OtsAppLogo = ({
  label,
  iconSrc,
  logoSrc,
  darkIconSrc,
  darkLogoSrc,
  logoAspectRatio,
  onClick,
}: OtsAppLogoProps) => {
  const dark = useOtsColorScheme() === 'dark';
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const hoverTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const cancelHover = () => {
    clearTimeout(hoverTimer.current);
    hoverTimer.current = undefined;
  };
  const collapse = () => {
    cancelHover();
    setHovered(false);
  };
  useEffect(() => cancelHover, []);
  const expanded = hovered || focused;
  return (
    <button
      type="button"
      aria-label={`${label} home`}
      onClick={onClick}
      onMouseMove={(event) => {
        const offset = event.clientX - event.currentTarget.getBoundingClientRect().left;
        if (offset > ICON_ZONE_PX) collapse();
        else if (!hovered && !hoverTimer.current) {
          hoverTimer.current = setTimeout(() => {
            hoverTimer.current = undefined;
            setHovered(true);
          }, HOVER_DELAY_MS);
        }
      }}
      onMouseLeave={collapse}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          collapse();
          setFocused(false);
        }
      }}
      style={{
        width: expanded ? 32 * logoAspectRatio + 8 : 40,
        maxWidth: 'calc(100vw - 2rem)',
      }}
      data-expanded={expanded}
      // `!overflow-hidden`: Mantine's normalize sets `button { overflow: visible }` outside any layer, which
      // would let the hidden wordmark hang over the category switcher and take its hovers and clicks.
      className="relative inline-flex h-10 shrink-0 items-center !overflow-hidden rounded bg-transparent transition-[width] duration-200 ease-out motion-reduce:transition-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--ots-primary)]"
    >
      <img
        src={dark ? darkIconSrc ?? iconSrc : iconSrc}
        alt=""
        width={32}
        height={32}
        className={`pointer-events-none absolute left-1 size-8 transition-opacity duration-150 motion-reduce:transition-none ${
          expanded ? 'opacity-0' : 'opacity-100'
        }`}
      />
      <img
        src={dark ? darkLogoSrc ?? logoSrc : logoSrc}
        alt=""
        aria-hidden="true"
        style={{ width: 32 * logoAspectRatio }}
        className={`pointer-events-none absolute left-1 h-8 max-w-[calc(100vw-2.5rem)] object-contain transition-opacity duration-150 motion-reduce:transition-none ${
          expanded ? 'opacity-100' : 'opacity-0'
        }`}
      />
    </button>
  );
};
